import { useEffect } from 'react';

interface GapReviewKeyboardHandlers {
  /** True while the reader is a form, which changes what ⌘↵ means. */
  isEditing: boolean;
  /**
   * True while a verdict is waiting on a confidence level. The number keys
   * answer that question instead of filing verdicts, and nothing else fires.
   */
  isConfirming: boolean;
  onPrevious: () => void;
  onNext: () => void;
  onKeep: () => void;
  onDrop: () => void;
  /** Which of the three levels was pressed, low-first. */
  onPickConfidenceLevel: (levelIndex: number) => void;
  onCancelConfirm: () => void;
  onCompose: () => void;
  onPostComment: () => void;
  onSaveEdits: () => void;
  onPreview: () => void;
  onToggleView: () => void;
}

/**
 * The review queue's shortcuts, bound at the document.
 *
 * `J` and `K` read backwards against vim, and that is on purpose: the mockup's
 * crumb bar labels `J Prev` and `K Next` with the keys sitting under the arrows
 * they drive, so the shortcuts match what is on screen rather than what a vim
 * user would guess. Whichever is wrong, the label and the binding agree.
 *
 * Three rules keep the shortcuts out of the way of typing:
 *
 * - Inside any text control nothing fires except ⌘↵ / Ctrl↵ to commit and Esc
 *   to blur. Otherwise composing "we should keep this" would keep, drop and
 *   comment. It is every control and not just a textarea because the edit form
 *   is mostly single-line inputs, and a severity of `0.2` typed into a bare
 *   `<input>` would otherwise register as drop.
 * - While the edit form is open, the queue keys do nothing at all. J is not
 *   worth losing a page of unsaved typing to, and 1 or 2 would file a verdict
 *   on a draft the reviewer is still rewriting.
 * - Any other modifier combination is left alone, so browser and OS shortcuts —
 *   ⌘K, Ctrl+P — still do what the reviewer expects.
 *
 * `1` and `2` file a verdict in two steps: they ask the reader for a confidence
 * level, and while that question is open `1` / `2` / `3` answer it and Esc takes
 * it back. The digits are reused rather than given their own row of keys
 * because only one of the two questions is ever on screen, and the bar says
 * which.
 *
 * ⌘↵ commits whatever is open: the edit form when there is one, the comment
 * otherwise. Two chords for two saves would be a keystroke to memorise for a
 * distinction the screen already makes.
 *
 * `R` swaps the reader between the product document and the run report. One key
 * rather than two: the reviewer wants to see the same gap the other way round,
 * and which of the two they are on is on screen already.
 */
export function useGapReviewKeyboard({
  isEditing,
  isConfirming,
  onPrevious,
  onNext,
  onKeep,
  onDrop,
  onPickConfidenceLevel,
  onCancelConfirm,
  onCompose,
  onPostComment,
  onSaveEdits,
  onPreview,
  onToggleView,
}: GapReviewKeyboardHandlers): void {
  useEffect(() => {
    function handleKeyDown(keyboardEvent: KeyboardEvent) {
      const target = keyboardEvent.target;
      const isTextEntry =
        target instanceof HTMLElement &&
        (target.tagName === 'TEXTAREA' ||
          target.tagName === 'INPUT' ||
          target.isContentEditable);

      const isCommitChord =
        keyboardEvent.key === 'Enter' &&
        (keyboardEvent.metaKey || keyboardEvent.ctrlKey);

      if (isCommitChord && (isEditing || isTextEntry)) {
        keyboardEvent.preventDefault();
        if (isEditing) onSaveEdits();
        else onPostComment();
        return;
      }

      if (isTextEntry) {
        if (keyboardEvent.key === 'Escape') target.blur();
        return;
      }

      if (
        keyboardEvent.metaKey ||
        keyboardEvent.ctrlKey ||
        keyboardEvent.altKey ||
        isEditing
      ) {
        return;
      }

      const key = keyboardEvent.key.toLowerCase();

      // A verdict is mid-air, so the number keys answer the confidence question
      // and nothing else fires: J with a picker open would leave a reviewer
      // deciding one gap while reading another.
      if (isConfirming) {
        if (key === 'escape') return onCancelConfirm();
        const levelIndex = ['1', '2', '3'].indexOf(key);
        if (levelIndex !== -1) return onPickConfidenceLevel(levelIndex);
        return;
      }

      if (key === 'j') return onPrevious();
      if (key === 'k') return onNext();
      if (key === '1') return onKeep();
      if (key === '2') return onDrop();
      if (key === 'p') return onPreview();
      if (key === 'r') return onToggleView();
      if (key === 'c') {
        // Without this the `c` lands in the textarea the handler just focused.
        keyboardEvent.preventDefault();
        onCompose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isEditing,
    isConfirming,
    onPrevious,
    onNext,
    onKeep,
    onDrop,
    onPickConfidenceLevel,
    onCancelConfirm,
    onCompose,
    onPostComment,
    onSaveEdits,
    onPreview,
    onToggleView,
  ]);
}
